/**
 * Plot Controls Toolbar
 * Reusable control toolbar for plot components
 */

import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import GridOnIcon from '@mui/icons-material/GridOn';
import GridOffIcon from '@mui/icons-material/GridOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { colors } from '../theme/colors';
import { shadows } from '../theme/shadows';

interface PlotControlsToolbarProps {
  onFullscreen: () => void;
  onResetZoom: () => void;
  onToggleGrid: () => void;
  onToggleLegend: () => void;
  onCopyData: () => void;
  onDownloadCSV: () => void;
  gridVisible: boolean;
  legendVisible: boolean;
}

export const PlotControlsToolbar: React.FC<PlotControlsToolbarProps> = ({
  onFullscreen,
  onResetZoom,
  onToggleGrid,
  onToggleLegend,
  onCopyData,
  onDownloadCSV,
  gridVisible,
  legendVisible,
}) => {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        gap: 0.5,
        backgroundColor: colors.neutral[50],
        border: `1px solid ${colors.neutral[200]}`,
        borderRadius: 2,
        padding: 0.5,
        boxShadow: shadows.sm,
      }}
    >
      <Tooltip title="Полноэкранный режим" placement="bottom">
        <IconButton
          onClick={onFullscreen}
          size="small"
          aria-label="Полноэкранный режим"
          sx={{
            color: colors.text.secondary,
            '&:hover': {
              color: colors.primary.main,
              backgroundColor: colors.neutral[100],
            },
          }}
        >
          <FullscreenIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Сбросить масштаб" placement="bottom">
        <IconButton
          onClick={onResetZoom}
          size="small"
          aria-label="Сбросить масштаб"
          sx={{
            color: colors.text.secondary,
            '&:hover': {
              color: colors.primary.main,
              backgroundColor: colors.neutral[100],
            },
          }}
        >
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={gridVisible ? 'Скрыть сетку' : 'Показать сетку'} placement="bottom">
        <IconButton
          onClick={onToggleGrid}
          size="small"
          aria-label={gridVisible ? 'Скрыть сетку' : 'Показать сетку'}
          sx={{
            color: colors.text.secondary,
            '&:hover': {
              color: colors.primary.main,
              backgroundColor: colors.neutral[100],
            },
          }}
        >
          {gridVisible ? <GridOnIcon fontSize="small" /> : <GridOffIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      <Tooltip title={legendVisible ? 'Скрыть легенду' : 'Показать легенду'} placement="bottom">
        <IconButton
          onClick={onToggleLegend}
          size="small"
          aria-label={legendVisible ? 'Скрыть легенду' : 'Показать легенду'}
          sx={{
            color: colors.text.secondary,
            '&:hover': {
              color: colors.primary.main,
              backgroundColor: colors.neutral[100],
            },
          }}
        >
          {legendVisible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      <Tooltip title="Копировать данные" placement="bottom">
        <IconButton
          onClick={onCopyData}
          size="small"
          aria-label="Копировать данные"
          sx={{
            color: colors.text.secondary,
            '&:hover': {
              color: colors.primary.main,
              backgroundColor: colors.neutral[100],
            },
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Скачать CSV" placement="bottom">
        <IconButton
          onClick={onDownloadCSV}
          size="small"
          aria-label="Скачать CSV"
          sx={{
            color: colors.text.secondary,
            '&:hover': {
              color: colors.primary.main,
              backgroundColor: colors.neutral[100],
            },
          }}
        >
          <FileDownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
